import { colors } from '@lmc/common/constants'
import DOMPurify from 'dompurify'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Text from '../../atoms/Text'
import { storedHtmlDomPurifyConfig } from '../../constants/storedHtmlDomPurifyConfig'
import { prepareRichEditorContent } from '../../dataEntry/RichEditor/normalizeRichEditorHtmlForStorage'
import { CHAR_LIMIT, textLength } from './utils'

const RichEditor = React.lazy(
  () => import('../../dataEntry/RichEditor/RichEditor')
)

type RichTextAreaFieldProps = {
  source: string
  value: string
  editMode: boolean
  printPDF?: boolean
  label: string
  onChangeHtml: (source: string, html: string) => void
}

export function RichTextAreaField({
  source,
  value,
  editMode,
  printPDF,
  label,
  onChangeHtml
}: RichTextAreaFieldProps) {
  const [charCount, setCharCount] = useState(() => textLength(value))

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setCharCount(textLength(value))
  }, [value, editMode])

  const handleChange = (html: string) => {
    setCharCount(textLength(html))
    onChangeHtml(source, html)
  }

  return (
    <div>
      <FieldLabel as={!printPDF ? 'p3' : 'h5'}>{label}</FieldLabel>
      {editMode && !printPDF ? (
        <>
          <React.Suspense fallback={null}>
            <RichEditor
              html={value}
              onChangeHtml={(html) => handleChange(html ?? '')}
            />
          </React.Suspense>
          <CharCounter $over={charCount > CHAR_LIMIT}>
            {charCount} / {CHAR_LIMIT}
          </CharCounter>
        </>
      ) : (
        <HtmlView
          // value is run through DOMPurify before injection
          // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              prepareRichEditorContent(value),
              storedHtmlDomPurifyConfig
            )
          }}
        />
      )}
    </div>
  )
}

const FieldLabel = styled(Text)`
  display: block;
  margin-top: 5px;
  color: ${colors.darkgrey};
`

const CharCounter = styled.div<{ $over: boolean }>`
  text-align: right;
  font-size: 12px;
  margin-top: 4px;
  color: ${({ $over }) => ($over ? '#d32f2f' : '#8c8c8c')};
`

const HtmlView = styled.div`
  p {
    margin: 0 0 0.35em;
  }
  p:last-child {
    margin-bottom: 0;
  }
  h1,
  h2,
  h3 {
    font-weight: 600;
    margin: 0 0 0.35em;
  }
  h1 {
    font-size: 1.25em;
  }
  h2 {
    font-size: 1.15em;
  }
  h3 {
    font-size: 1.05em;
  }
  ul,
  ol {
    margin: 0 0 0.35em 1.25em;
    padding: 0;
  }
  li {
    margin: 0.15em 0;
  }
  a {
    color: ${({ theme }) => theme.color.status.primary};
    text-decoration: underline;
  }
`
